import React, { ReactNode, FC } from 'react';
import {
  Zoom,
  toast,
  ToastContainer,
  ToastPosition,
  ToastPromiseParams,
  Id,
  ToastOptions,
} from 'react-toastify';
import styled from 'styled-components';
import { KitToastTypeOptions, TranslateFunc } from '../common/types';
import KitIcon from './KitIcon';
import KitButton from './KitButton';
import colors from '../common/theme/colors';
import constants, { ToastDuration } from '../common/theme/constants';
import 'react-toastify/dist/ReactToastify.css';

const StyledToastContainer = styled(ToastContainer)`
  &&&.Toastify__toast-container {
    width: auto;
  }

  .Toastify__toast {
    padding: ${constants.spacing.absolute.xs}px;
    color: ${colors.gray_90};
    min-height: 44px;
  }

  .Toastify__toast-body {
    color: inherit;
    padding: ${constants.spacing.absolute.xs}px;
    gap: ${constants.spacing.absolute.xs}px;
  }
`;

export interface KitToastProps {
  t: TranslateFunc;
  toastType: KitToastTypeOptions;
  message: string;
  position?: ToastPosition;
  duration?: number | false | undefined;
  customClasses?: string;
  customStyles?: Record<string, unknown>;
  customBody?: ReactNode;
  customIcon?: ReactNode;
  promiseCall?: () => Promise<unknown>;
  promiseMessages?: ToastPromiseParams;
  hasClose?: boolean;
  containerId?: string;
  progress?: number;
  toastId?: number;
}

export interface KitToastContainerProps {
  duration: number;
  position: ToastPosition;
  reverseOrder: boolean;
  containerId?: string;
}

export const useToast = ({
  t,
  toastType,
  message,
  position,
  duration = ToastDuration.short,
  customClasses,
  customStyles,
  customBody,
  customIcon,
  hasClose,
  containerId,
  progress,
  toastId,
}: KitToastProps): Id => {
  const style = customStyles ?? { borderRadius: 0 };
  const additionalArgs: ToastOptions = {
    style,
    autoClose: duration,
    position,
    icon: customIcon,
    className: customClasses,
    containerId,
    progress,
    hideProgressBar: progress === undefined,
    toastId,
  };
  const renderContent = () => (customBody ? <>{customBody}</> : message);
  switch (toastType) {
    case KitToastTypeOptions.SUCCESS:
      return toast.success(renderContent(), {
        ...additionalArgs,
      });
    case KitToastTypeOptions.ERROR:
      return toast.error(renderContent(), {
        ...additionalArgs,
        icon: (
          <KitIcon icon='close-circle' size={`${constants.iconSize.l}rem`} fill={colors.red_50} />
        ),
      });
    case KitToastTypeOptions.INFO:
      return toast(renderContent(), {
        ...additionalArgs,
        icon: (
          <KitIcon
            icon='information-outline'
            size={`${constants.iconSize.l}rem`}
            fill={colors.blue_55}
          />
        ),
      });
    case KitToastTypeOptions.WARNING:
      return toast(renderContent(), {
        ...additionalArgs,
        icon: (
          <KitIcon icon='warning' size={`${constants.iconSize.l}rem`} fill={colors.orange_55} />
        ),
      });
    case KitToastTypeOptions.CUSTOM:
      return toast(
        (toastEl) => (
          <div>
            {customBody ?? message}
            {hasClose && (
              <KitButton onClick={() => toastEl.closeToast()}>{t('kit:button.close')}</KitButton>
            )}
          </div>
        ),
        { ...additionalArgs }
      );
    default:
      return toast(message);
  }
};

const KitToast: FC<KitToastContainerProps> = ({
  duration,
  position,
  reverseOrder,
  containerId,
}) => {
  return (
    <StyledToastContainer
      role='alert'
      position={position}
      autoClose={duration}
      hideProgressBar
      closeButton={false}
      transition={Zoom}
      newestOnTop={!reverseOrder}
      enableMultiContainer
      containerId={containerId}
    />
  );
};

export default KitToast;
